import _ from "lodash";
import React from "react";
import { unstable_batchedUpdates as batchUpdates } from "react-dom";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { Scope } from "../generated/types";
import { Project } from "../types";
import { bindActionCreators } from "../utils/react-utils";
import apps, { App } from "./apps";

interface State {
  app: App | null;
  project: Project | null;
}

interface SetAppAction {
  type: "SET_APP";
  payload: App | null;
}

const setApp = (app: App | null): SetAppAction => ({
  type: "SET_APP",
  payload: app,
});

interface SetProjectAction {
  type: "SET_PROJECT";
  payload: Project | null;
}

const setProject = (project: Project | null): SetProjectAction => ({
  type: "SET_PROJECT",
  payload: project,
});

type Action = SetAppAction | SetProjectAction;

interface Actions {
  setApp: (app: App | null) => void;
  setProject: (project: Project | null) => void;
}

export interface AppContextValue extends State, Actions {}

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "SET_APP":
      if (action.payload == null) {
        return { ...state, app: action.payload };
      }
      if (apps[action.payload].selectProject) {
        return { ...state, app: action.payload };
      } else {
        return { ...state, app: action.payload, project: null };
      }
    case "SET_PROJECT":
      return { ...state, project: action.payload };
    default:
      return state;
  }
};

const constructRoute = (app: App | null, project: Project | null) => {
  if (app == null) {
    return "/";
  }
  const config = apps[app];
  if (config.selectProject && project != null) {
    return `${config.route}/${project.customer}/${project.project}`;
  } else {
    return config.route;
  }
};

const AppContext = React.createContext<AppContextValue | undefined>(undefined);

const parseRoute = (
  route: string,
): { app: App; project: Project | null } | undefined => {
  const match = matchPath(
    {
      path: "/:app/:customer?/:project?",
      caseSensitive: false,
      end: true,
    },
    route,
  );

  if (match == null) {
    return undefined;
  }

  const params = match.params;
  const app = params.app;
  const customer = params.customer;
  const project = params.project;

  const matchedApp = (Object.keys(apps) as App[]).find(
    (a) => apps[a].route === `/${app}`,
  );
  if (matchedApp == null) {
    return undefined;
  }
  return {
    app: matchedApp,
    project:
      customer != null && project != null
        ? { customer, project, scope: Scope.Internal }
        : null,
  };
};

const stateInitialiser = ({
  navigate,
  location,
}: {
  navigate: ReturnType<typeof useNavigate>;
  location: ReturnType<typeof useLocation>;
}): State => {
  const route = parseRoute(location.pathname);

  if (route == null) {
    navigate(constructRoute(null, null));
    return { app: null, project: null };
  }

  return { app: route.app, project: route.project ?? null };
};

export const Provider = (props: React.PropsWithChildren<any>) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [state, dispatch] = React.useReducer<
    React.Reducer<State, Action>,
    {
      navigate: ReturnType<typeof useNavigate>;
      location: ReturnType<typeof useLocation>;
    }
  >(reducer, { navigate, location }, stateInitialiser);

  // A flag to track if we're navigating programmatically
  // to prevent update loops
  const navigationInProgress = React.useRef(false);

  // Only update the URL when app/project state changes
  React.useEffect(() => {
    // Skip if we're in the middle of handling a location change
    if (navigationInProgress.current) {
      return;
    }

    const targetPath = constructRoute(state.app, state.project);
    // Only navigate if the path is different
    if (location.pathname !== targetPath) {
      navigationInProgress.current = true;
      navigate(targetPath);
      // Reset the flag after a short delay
      setTimeout(() => {
        navigationInProgress.current = false;
      }, 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, state.app, state.project]);

  // Handle URL changes that come from outside (like browser navigation)
  React.useEffect(() => {
    // Skip if we're in the middle of a programmatic navigation
    if (navigationInProgress.current) {
      return;
    }

    const route = parseRoute(location.pathname);
    if (route != null) {
      // Only update state if something changed
      const appChanged = route.app !== state.app;
      const projectChanged = !_.isEqual(route.project, state.project);

      if (appChanged || projectChanged) {
        batchUpdates(() => {
          if (appChanged) {
            dispatch(setApp(route.app));
          }
          if (projectChanged) {
            dispatch(setProject(route.project));
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const actions = React.useMemo(
    () =>
      bindActionCreators(
        {
          setApp,
          setProject,
        },
        dispatch,
      ),
    [dispatch],
  );

  const context = React.useMemo(
    () => ({
      ...state,
      ...actions,
    }),
    [state, actions],
  );

  return (
    <AppContext.Provider value={context}>{props.children}</AppContext.Provider>
  );
};

export const useAppState = () => {
  const contextValue = React.useContext(AppContext);
  if (contextValue == null) {
    throw new Error("Cannot useAppState outside of a Provider");
  }
  return contextValue;
};

export const useProject = () => {
  const state = useAppState();
  if (state.project == null) {
    throw new Error("Cannot call useProject: No project selected.");
  }
  return state.project;
};
